import { ITranscriptionSegment } from 'Modules/videoHistory/types/transcriptions.types';

const MAX_TIME_BETWEEN_SEGMENTS_IN_MS = 2 * 60 * 1000;

/**
 * Formats elapsed time between two instants as `mm:ss` (total whole minutes, padded seconds).
 * @param fromMs - Start instant (unix milliseconds), e.g. transcription start.
 * @param toMs - End instant (unix milliseconds), e.g. segment timestamp.
 */
export function formatElapsedMmSs(fromMs: number, toMs: number): string {
  const elapsedSec = Math.max(0, Math.floor((toMs - fromMs) / 1000));
  const mm = String(Math.floor(elapsedSec / 60)).padStart(2, '0');
  const ss = String(elapsedSec % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

/**
 * Merges sequential segments that belong to the same person.
 * Does not merge segments that contain no personId.
 * @param segments - The segments to merge.
 * @returns The merged segments.
 */
export const mergeSegments = (
  segments: ITranscriptionSegment[] | undefined | null
): ITranscriptionSegment[] | null => {
  if (!segments) {
    return null;
  }

  return segments.reduce<ITranscriptionSegment[]>(
    (acc, currentSegment, index) => {
      const previousMergedSegment = acc[acc.length - 1];
      const previousPureSegment = segments[index - 1];

      /**
       * If the previous merged segment doesn't have a personId, or if the current segment doesn't have a personId,
       * or if the previous merged segment has a different personId than the current segment,
       * or if the time between the previous merged segment and the current segment is greater than 2 minutes,
       * then we need to start a new merged segment.
       */
      if (
        !previousMergedSegment?.personId ||
        !currentSegment.personId ||
        previousMergedSegment.personId !== currentSegment.personId ||
        (previousPureSegment?.timestamp &&
          previousPureSegment.timestamp.valueOf() +
            MAX_TIME_BETWEEN_SEGMENTS_IN_MS <
            currentSegment.timestamp.valueOf())
      ) {
        acc.push(currentSegment);
        return acc;
      }

      /**
       * If the time between the previous merged segment and the current segment is less than 2 minutes,
       * and the previous merged segment has the same personId as the current segment,
       * then we need to add the current segment's text to the previous merged segment's text.
       */
      acc[acc.length - 1] = {
        ...previousMergedSegment,
        text: Array.isArray(previousMergedSegment.text)
          ? [...previousMergedSegment.text, currentSegment.text as string]
          : [previousMergedSegment.text, currentSegment.text as string],
      };
      return acc;
    },
    []
  );
};
